import { useState, type FormEvent } from 'react'
import axios from 'axios'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Trash2 } from 'lucide-react'
import { aplicacaoApi, perfilApi, usuarioApi } from '@/services/api'
import type { Aplicacao, PaginatedResult, Perfil, Usuario, UsuarioPerfilAplicacao } from '@/types/api'
import { Modal } from '@/components/ui/Modal'
import { useToast } from '@/components/ui/Toast'

interface Props {
  usuario: Usuario
  onClose: () => void
}

const inputClass = 'w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary-500'
const labelClass = 'block text-sm font-medium text-slate-300 mb-1.5'

export function UserAccessModal({ usuario, onClose }: Props) {
  const queryClient = useQueryClient()
  const { addToast } = useToast()
  const [aplicacaoId, setAplicacaoId] = useState('')
  const [perfilId, setPerfilId] = useState('')
  const [isPrincipal, setIsPrincipal] = useState(true)
  const [saving, setSaving] = useState(false)

  const assignmentsQuery = useQuery<UsuarioPerfilAplicacao[]>({
    queryKey: ['usuario-perfis', usuario.id],
    queryFn: () => usuarioApi.profiles(usuario.id).then(response => response.data)
  })
  const applicationsQuery = useQuery<PaginatedResult<Aplicacao>>({
    queryKey: ['aplicacoes', 'assignment'],
    queryFn: () => aplicacaoApi.list({ page: 1, pageSize: 100, ativa: true }).then(response => response.data)
  })
  const profilesQuery = useQuery<PaginatedResult<Perfil>>({
    queryKey: ['perfis', usuario.empresaId, 'assignment'],
    queryFn: () => perfilApi.list(usuario.empresaId!, { page: 1, pageSize: 100 }).then(response => response.data),
    enabled: Boolean(usuario.empresaId)
  })

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!aplicacaoId || !perfilId) return

    setSaving(true)
    try {
      await usuarioApi.assignProfile(usuario.id, {
        usuarioId: usuario.id,
        perfilId,
        aplicacaoId,
        isPrincipal,
        dataExpiracao: null
      })
      await queryClient.invalidateQueries({ queryKey: ['usuario-perfis', usuario.id] })
      addToast({ type: 'success', title: 'Acesso atribuído com sucesso' })
      setPerfilId('')
      setAplicacaoId('')
    } catch (error) {
      const description = axios.isAxiosError(error)
        ? (error.response?.data?.detail || error.response?.data?.message || 'Verifique empresa, licença e perfil.')
        : 'Não foi possível atribuir o acesso.'
      addToast({ type: 'error', title: 'Erro ao atribuir acesso', description: String(description) })
    } finally {
      setSaving(false)
    }
  }

  const remove = async (assignment: UsuarioPerfilAplicacao) => {
    try {
      await usuarioApi.removeProfile(usuario.id, assignment.perfilId, assignment.aplicacaoId)
      await queryClient.invalidateQueries({ queryKey: ['usuario-perfis', usuario.id] })
      addToast({ type: 'success', title: 'Acesso removido' })
    } catch (error) {
      const description = axios.isAxiosError(error)
        ? (error.response?.data?.detail || error.response?.data?.message || 'Não foi possível remover o acesso.')
        : 'Não foi possível remover o acesso.'
      addToast({ type: 'error', title: 'Erro ao remover acesso', description: String(description) })
    }
  }

  const applications = applicationsQuery.data?.items ?? []
  const profiles = (profilesQuery.data?.items ?? []).filter(profile => profile.empresaId === usuario.empresaId)
  const assignments = assignmentsQuery.data ?? []

  return (
    <Modal isOpen onClose={onClose} title={`Acessos de ${usuario.nome}`} size="lg">
      {!usuario.empresaId ? (
        <p className="text-amber-400">O usuário ainda não possui empresa vinculada. Atualize o cadastro antes de atribuir acessos.</p>
      ) : (
        <div className="space-y-6">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label>
              <span className={labelClass}>Aplicação *</span>
              <select className={inputClass} value={aplicacaoId} onChange={event => setAplicacaoId(event.target.value)} required>
                <option value="">Selecione uma aplicação</option>
                {applications.map(application => (
                  <option key={application.id} value={application.id}>{application.nome}</option>
                ))}
              </select>
            </label>
            <label>
              <span className={labelClass}>Perfil *</span>
              <select className={inputClass} value={perfilId} onChange={event => setPerfilId(event.target.value)} required>
                <option value="">Selecione um perfil</option>
                {profiles.map(profile => (
                  <option key={profile.id} value={profile.id}>{profile.nome}</option>
                ))}
              </select>
            </label>
            <label className="md:col-span-2 flex items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" checked={isPrincipal} onChange={event => setIsPrincipal(event.target.checked)} />
              Perfil principal nessa aplicação
            </label>
            {profilesQuery.isSuccess && profiles.length === 0 && (
              <p className="md:col-span-2 text-sm text-amber-400">Nenhum perfil ativo pertence à empresa deste usuário.</p>
            )}
            <div className="md:col-span-2 flex justify-end">
              <button type="submit" disabled={saving || applicationsQuery.isLoading || profilesQuery.isLoading || profiles.length === 0} className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Atribuir acesso
              </button>
            </div>
          </form>

          <div className="border-t border-slate-800 pt-4">
            <h3 className="text-sm font-semibold text-white mb-3">Acessos atuais</h3>
            {assignmentsQuery.isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin text-primary-400" />
            ) : assignments.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhum perfil vinculado a uma aplicação.</p>
            ) : (
              <div className="space-y-2">
                {assignments.map(assignment => (
                  <div key={`${assignment.perfilId}-${assignment.aplicacaoId}`} className="flex items-center justify-between rounded-lg bg-slate-950 border border-slate-800 px-3 py-2">
                    <div>
                      <p className="text-sm text-white">{assignment.nomeAplicacao}</p>
                      <p className="text-xs text-slate-400">{assignment.nomePerfil}{assignment.isPrincipal ? ' · principal' : ''}</p>
                    </div>
                    <button type="button" onClick={() => void remove(assignment)} className="p-2 rounded-md text-slate-400 hover:text-red-400 hover:bg-red-500/10" title="Remover acesso">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}
